import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { UserCheck, CalendarPlus, ChevronDown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collaborateurs CDD/CDI sous contrat aujourd'hui sans AF ni activité affectée
export const FormateursDisponiblesCard = ({ headers }) => {
  const [data, setData] = useState(null);
  const [showOccupes, setShowOccupes] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/dashboard/formateurs-disponibles`, { headers }).then(r => setData(r.data)).catch(() => setData({ disponibles: [], occupes: [] }));
  }, [headers]);
  if (!data) return null;
  const { disponibles = [], occupes = [], is_weekend, is_ferie } = data;
  const initials = (f) => `${(f.prenom || '')[0] || ''}${(f.nom || '')[0] || ''}`.toUpperCase();
  return (
    <Card className={`border-0 shadow-sm ${disponibles.length > 0 ? 'ring-1 ring-amber-200' : ''}`} data-testid="formateurs-disponibles-card">
      <CardHeader className="border-b bg-slate-50/50 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base font-['Plus_Jakarta_Sans']">
            <UserCheck className={`w-4 h-4 ${disponibles.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
            Collaborateurs CDD / CDI sans affectation aujourd'hui
            <span className="text-xs font-normal text-slate-400 ml-1" data-testid="formateurs-disponibles-count">({disponibles.length})</span>
          </CardTitle>
          <span className="text-xs text-slate-400 capitalize">{format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}{is_ferie ? ' · jour férié' : is_weekend ? ' · week-end' : ''}</span>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {disponibles.length === 0 ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="formateurs-disponibles-vide"><UserCheck className="w-4 h-4" /> Tous les collaborateurs sous contrat ont une formation ou une activité affectée aujourd'hui.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="formateurs-disponibles-list">
            {disponibles.map(f => (
              <Link key={f.id} to="/planning?tab=activites" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 hover:bg-amber-100 transition-colors group" title="Affecter une activité dans le planning" data-testid={`formateur-dispo-${f.id}`}>
                <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-[11px] font-semibold flex items-center justify-center">{initials(f)}</span>
                <div className="leading-tight">
                  <p className="text-sm font-medium text-slate-800">{f.nom} {f.prenom}</p>
                  <p className="text-[10px] text-amber-700 font-semibold">{f.type_contrat} · aucune tâche affectée</p>
                </div>
                <CalendarPlus className="w-3.5 h-3.5 text-amber-400 group-hover:text-amber-700 ml-1" />
              </Link>
            ))}
          </div>
        )}
        {occupes.length > 0 && (
          <div>
            <button type="button" onClick={() => setShowOccupes(v => !v)} className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="toggle-formateurs-occupes">
              <ChevronDown className={`w-3 h-3 transition-transform ${showOccupes ? 'rotate-180' : ''}`} /> {occupes.length} collaborateur(s) affecté(s) aujourd'hui
            </button>
            {showOccupes && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="formateurs-occupes-list">
                {occupes.map(f => (
                  <span key={f.id} className="text-[11px] px-2 py-1 rounded-md bg-slate-100 text-slate-600"><span className="font-medium text-slate-800">{f.nom} {f.prenom}</span> · {f.type_contrat} · {f.occupation}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
